// @ts-nocheck

/**
 * Recharge Widget Custom Integration – Stemregen / Horizon Theme
 * --------------------------------------------------------------
 * This script ensures proper Recharge widget behavior based on the selected variant.
 * It:
 *  - Loads the Recharge product JSON to check which variants have a subscription plan
 *  - Hides the widget when a non-subscription variant is selected
 *  - Updates the page URL with ?variant= and ?selling_plan= params
 *  - Formats subscription price display (strikeout + "SAVE $X each month")
 *
 * Designed to work with the Horizon theme without editing core Shopify files.
 * To debug, toggle `DEBUG` below.
 */

(function () {
  const DEBUG = false;
  const log = (...args) => DEBUG && console.log("🔄 Recharge:", ...args);

  const SELECTORS = {
    variantInput: 'input[name="id"]',
    rechargeWidget: ".rc-container-wrapper",
    activeSellingPlanRadio: ".rc-option--active input[type=radio]",
  };

  let rechargeVariantIds = [];
  let rechargeJson = null;
  let isB2B = false; // Track B2B customer status

  /**
   * Detect if current customer is B2B/Wholesale
   * Checks multiple indicators for B2B status
   */
  function isB2BCustomer() {
    // Check for wholesale variant selection
    const variantInputs = document.querySelectorAll('input[type="radio"][name*="variant"]');
    for (const input of variantInputs) {
      if (input.checked && (input.value.includes('Wholesale') || 
          input.getAttribute('aria-label')?.includes('Wholesale'))) {
        log("🏢 B2B detected via wholesale variant selection");
        return true;
      }
    }
    
    // Check for wholesale in selected variant title
    const selectedVariantTitle = document.querySelector('.variant-option__button-label input:checked')?.getAttribute('aria-label');
    if (selectedVariantTitle && selectedVariantTitle.includes('Wholesale')) {
      log("🏢 B2B detected via selected variant title");
      return true;
    }
    
    // Check for B2B-specific classes or elements (added by Liquid templates)
    if (document.querySelector('[data-b2b-customer="true"]') || 
        document.body.classList.contains('customer-b2b')) {
      log("🏢 B2B detected via DOM attributes");
      return true;
    }
    
    // Check URL for wholesale variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      const variantElement = document.querySelector(`[data-variant-id="${variantId}"]`);
      if (variantElement?.textContent?.includes('Wholesale')) {
        log("🏢 B2B detected via URL variant parameter");
        return true;
      }
    }
    
    return false;
  }

  /**
   * Get currently selected variant ID from hidden input
   */
  function getCurrentVariantId() {
    return (
      document.querySelector(SELECTORS.variantInput)?.value?.trim() || null
    );
  }

  /**
   * Get the selected selling plan ID from the Recharge widget (if any)
   */
  function getCurrentSellingPlanId() {
    const el = document.querySelector(SELECTORS.activeSellingPlanRadio);
    if (!el || el.value === "onetime") return null;
    const match = el.name?.match(/selling_plan_(\d+)/);
    return match?.[1] || null;
  }

  /**
   * Update URL to include ?variant= and optionally &selling_plan=
   */
  function updateUrlParams(variantId, sellingPlanId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    if (sellingPlanId) {
      url.searchParams.set("selling_plan", sellingPlanId);
    } else {
      url.searchParams.delete("selling_plan");
    }
    window.history.replaceState({}, "", url.toString());
    log("✅ URL updated:", url.toString());
  }

  /**
   * Show or hide the Recharge widget based on eligibility
   */
  function toggleRechargeWidget(show) {
    const widget = document.querySelector(SELECTORS.rechargeWidget);
    if (widget) {
      // Always hide for B2B customers regardless of variant eligibility
      const shouldShow = show && !isB2B;
      widget.style.display = shouldShow ? "" : "none";
      log(shouldShow ? "✅ Widget visible" : "🚫 Widget hidden" + (isB2B ? " (B2B customer)" : ""));

      // Fix: if showing, wait a bit and re-format Recharge price labels
      if (shouldShow) {
        setTimeout(() => {
          const variantId = getCurrentVariantId();
          formatRechargeDisplayText(variantId);
          log("🛠 Re-applied Recharge formatting after widget show.");
        }, 300); // Adjust if needed, 300–500ms is usually sufficient
      }
    }
  }

  /**
   * Format subscription pricing display with original + discounted + save amount
   */
  function formatRechargeDisplayText(variantId) {
    try {
      const variant = rechargeJson?.product?.variants?.find(
        (v) => v.external_variant_id === String(variantId)
      );
      if (!variant) return;

      const priceBlock = variant?.prices?.[0];
      const plan = priceBlock?.plans?.find(
        (p) => p.id && p.discounted_price && p.discount_value
      );
      if (!plan) return;

      const basePrice = parseFloat(priceBlock.unit_price);
      const discounted = parseFloat(plan.discounted_price);
      const discountVal = parseFloat(plan.discount_value);

      if (!basePrice || !discounted || isNaN(discountVal)) return;

      const subPriceEl = document.querySelector("[data-price-subsave]");
      const discountLabel = document.querySelector("[data-label-discount]");
      const subTextEl = document.querySelector("[data-label-text-subsave]");

      // Clean subscription label
      if (subTextEl?.textContent?.includes("##separator##")) {
        subTextEl.textContent = subTextEl.textContent
          .replace(/##separator##/g, "")
          .trim();
      }

      // Apply strike-through and save amount
      if (subPriceEl) {
        const saveAmt = Math.round(discountVal);
        subPriceEl.innerHTML = `<span><s>$${Math.round(
          basePrice
        ).toLocaleString()}</s> $${Math.round(
          discounted
        ).toLocaleString()}</span> <span class="save--content-custom">SAVE $${Math.round(
          saveAmt
        ).toLocaleString()} each month</span>`;
      }

      if (discountLabel) discountLabel.textContent = "";

      // Clean up one-time label too
      const oneTimeTextEl = document.querySelector("[data-label-text-onetime]");
      if (oneTimeTextEl?.textContent?.includes("##separator##")) {
        oneTimeTextEl.textContent = oneTimeTextEl.textContent
          .replace(/##separator##/g, "")
          .trim();
      }

      log("✨ Recharge display updated:", {
        basePrice,
        discounted,
        discountVal,
      });
    } catch (err) {
      log("⚠️ Error formatting Recharge display:", err);
    }
  }

  /**
   * Fetch Recharge product JSON and extract eligible variant IDs
   */
  async function fetchRechargeVariantIds(productId) {
    const url = `https://static.rechargecdn.com/store/kalyagen.myshopify.com/product/2022-06/${productId}.json`;
    try {
      const res = await fetch(url);
      const json = await res.json();
      rechargeJson = json; // Store globally

      // Extract eligible variant IDs based on plan presence inside each variant's pricing
      rechargeVariantIds =
        json?.product?.variants
          ?.filter((variant) =>
            variant?.prices?.[0]?.plans?.some((plan) => {
              const planDetails = json?.product?.plans?.find(
                (p) => p.id === plan.id
              );
              return planDetails?.type === "subscription";
            })
          )
          .map((v) => String(v.external_variant_id)) || [];

      log("✅ Recharge variant IDs loaded:", rechargeVariantIds);
    } catch (err) {
      log("❌ Failed to load Recharge variant IDs", err);
    }
  }

  /**
   * Append selected variant label to the Price legend
   */
  function updateLegendWithVariantLabel() {
    try {
      const legendEl = document.querySelector(
        'fieldset[data-option-id*="price"] legend'
      );
      const activeRadio = document.querySelector(
        '.variant-option__button-label input[type="radio"]:checked'
      );

      const label =
        activeRadio?.getAttribute("aria-label") || activeRadio?.value || null;

      if (legendEl && label) {
        legendEl.textContent = `Price – ${label}`;
        log("🏷️ Updated legend text:", legendEl.textContent);
      }
    } catch (err) {
      log("⚠️ Failed to update legend label:", err);
    }
  }

  /**
   * Runs all logic for toggling, updating, formatting
   */
  function handleRechargeState() {
    // Update B2B status on each state change
    isB2B = isB2BCustomer();
    
    const variantId = getCurrentVariantId();
    const sellingPlanId = getCurrentSellingPlanId();
    const isEligible = rechargeVariantIds.includes(String(variantId));

    updateUrlParams(variantId, sellingPlanId);
    toggleRechargeWidget(isEligible);

    if (isEligible && !isB2B) {
      formatRechargeDisplayText(variantId);
    }

    updateLegendWithVariantLabel(); // ✅ Add legend update here
  }

  /**
   * Extract Recharge product ID from input element ID (e.g. product_8085xxxx_subsave)
   */
  async function getRechargeProductIdFromWidget() {
    const input = document.querySelector('input[id^="product_"][type="radio"]');
    const match = input?.id?.match(/product_(\d+)_/);
    return match?.[1] || null;
  }

  /**
   * Observe the hidden variant input field for value changes
   */
  function watchVariantChange(callback) {
    const input = document.querySelector(SELECTORS.variantInput);
    if (!input) return;

    let oldValue = input.value;
    const observer = new MutationObserver(() => {
      const newValue = input.value;
      if (newValue !== oldValue) {
        oldValue = newValue;
        callback();
      }
    });

    observer.observe(input, { attributes: true, attributeFilter: ["value"] });
    log("👁️ Watching for variant changes.");
  }

  /**
   * Initialize everything: wait for widget, fetch Recharge JSON, bind watchers
   */
  async function initRecharge() {
    log("⏳ Initializing Recharge script...");

    // Check B2B status early
    isB2B = isB2BCustomer();
    if (isB2B) {
      log("🏢 B2B customer detected - Recharge widget will be hidden");
    }

    const widgetReady = await waitForElement(SELECTORS.rechargeWidget);
    if (!widgetReady) return log("❌ Widget never appeared.");

    // Hide widget immediately for B2B customers
    if (isB2B) {
      const widget = document.querySelector(SELECTORS.rechargeWidget);
      if (widget) {
        widget.style.display = "none";
        log("🚫 Widget hidden for B2B customer");
      }
    }

    const productId = await getRechargeProductIdFromWidget();
    if (!productId) return log("❌ No product ID from Recharge widget.");

    await fetchRechargeVariantIds(productId);
    handleRechargeState();

    watchVariantChange(handleRechargeState);

    const form = document.querySelector("form[action*='/cart/add']");
    if (form) {
      form.addEventListener("change", (e) => {
        if (e.target?.name?.startsWith("selling_plan")) {
          handleRechargeState();
        }
      });
    }

    log("✅ Recharge script initialized.");
  }

  /**
   * Wait for a DOM element to appear (with retries)
   */
  function waitForElement(selector, maxTries = 20, interval = 300) {
    return new Promise((resolve) => {
      let tries = 0;
      const check = () => {
        const el = document.querySelector(selector);
        if (el) return resolve(true);
        if (++tries >= maxTries) return resolve(false);
        setTimeout(check, interval);
      };
      check();
    });
  }

  document.addEventListener("DOMContentLoaded", initRecharge);
})();
